import React from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Box, Typography, styled, useTheme } from '@mui/material';

const StyledEditorContainer = styled(Box)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    '& .quill': {
      backgroundColor: isDark ? theme.palette.action.hover : theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`,
      transition: 'border-color 0.2s ease',
      '&:hover': {
        borderColor: theme.palette.text.primary,
      },
      '&:focus-within': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
    '& .ql-toolbar': {
      backgroundColor: isDark ? theme.palette.action.selected : theme.palette.action.hover,
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
      border: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .ql-container': {
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      border: 'none',
      fontFamily: theme.typography.fontFamily,
      fontSize: '1rem',
      minHeight: '150px',
    },
    '& .ql-editor': {
      minHeight: '150px',
      color: theme.palette.text.primary,
      '&.ql-blank::before': {
        color: theme.palette.text.secondary,
        fontStyle: 'normal',
      },
    },
    // Toolbar icon colors — only needed for dark mode
    ...(isDark && {
      '& .ql-stroke': {
        stroke: `${theme.palette.text.secondary} !important`,
      },
      '& .ql-fill': {
        fill: `${theme.palette.text.secondary} !important`,
      },
      '& .ql-picker-label': {
        color: `${theme.palette.text.secondary} !important`,
      },
      '& .ql-picker-options': {
        backgroundColor: `${theme.palette.background.paper} !important`,
        border: `1px solid ${theme.palette.divider} !important`,
      },
      '& .ql-picker-item': {
        color: `${theme.palette.text.primary} !important`,
        '&:hover': {
          color: `${theme.palette.primary.main} !important`,
        },
      },
      '& .ql-toolbar button:hover .ql-stroke, & .ql-toolbar button.ql-active .ql-stroke': {
        stroke: `${theme.palette.primary.main} !important`,
      },
      '& .ql-toolbar button:hover .ql-fill, & .ql-toolbar button.ql-active .ql-fill': {
        fill: `${theme.palette.primary.main} !important`,
      },
    }),
  };
});

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  /** @deprecated No longer used — theme is auto-detected from MUI context */
  theme?: 'dark' | 'light';
  helperText?: string;
  minHeight?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  minHeight = 150,
}) => {
  const muiTheme = useTheme();

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link'],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list',
    'indent',
    'link',
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          {label}
        </Typography>
      )}
      <StyledEditorContainer theme={muiTheme}>
        <ReactQuill
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          theme="snow"
          style={{ minHeight: `${minHeight}px` }}
        />
      </StyledEditorContainer>
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            ml: 1.5,
            display: 'block',
            color: 'text.secondary',
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default RichTextEditor;
